import { useTranslation } from "react-i18next"
import { useEffect, useState, useMemo, useRef } from "react"
import { GridColDef, GridRowsProp } from "@mui/x-data-grid"

import ProfileAvatar from "@/components/profileAvatar/ProfileAvatar"
import Icon from "@/components/icon/Icon"
import { colors } from "@/components/colors"
import Pagination from "@/components/table/Pagination"
import Table from "@/components/table/Table"
import { useTeamCoachListQuery } from "@/hooks/api/useCoachQuery"

import "./CoachTable.scss"
import Chip from "@/components/chip/Chip"
import { createAccountStore } from "@/stores/account"

export default function CoachTable({
  selectedId,
  setSelectedId,
  searchKeyword,
}: {
  selectedId: number
  setSelectedId: (id: number) => void
  searchKeyword: string
}) {
  const { info } = createAccountStore()
  const { data } = useTeamCoachListQuery()
  const { t } = useTranslation("")
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 5,
  })
  const currentCoachRef = useRef<number | null>(null)

  const [filteredRows, setFilteredRows] = useState<GridRowsProp>([])
  const [rows, setRows] = useState<GridRowsProp>([])

  useEffect(() => {
    if (selectedId) {
      currentCoachRef.current = selectedId
    }
  }, [selectedId])

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "name",
        flex: 1,
        renderHeader: () => t("name"),
        renderCell: (params) => (
          <>
            <div className="coach-cell">
              <div className="coach-content">
                <ProfileAvatar imageUrl={params.row.image} size={42} />
                <span className="coach-label">{params.row.name}</span>
                {info?.email === params.row.email && <Chip type="default-small" label="Me" />}
              </div>
            </div>
          </>
        ),
      },
      {
        field: "idfCoach",
        headerName: "",
        renderCell: (params) => {
          return (
            <div className="coach-icon">
              {params.row.id === currentCoachRef.current && (
                <Icon type="arrow-right" size={20} color={colors.neutral.grey[600]} />
              )}
            </div>
          )
        },
        maxWidth: 40,
      },
    ],
    [t],
  )

  useEffect(() => {
    setRows(
      (data || []).map((item) => ({
        ...item,
        id: item.idfCoach,
      })),
    )
  }, [data])

  useEffect(() => {
    setFilteredRows(
      rows.filter((row) => row.name.toLowerCase().includes(searchKeyword.toLowerCase())),
    )
  }, [searchKeyword, rows])

  return (
    <div className="CoachTableCont">
      <Table
        rows={filteredRows}
        columns={columns}
        rowHeight={66}
        columnHeaderHeight={40}
        columnBufferPx={0}
        sx={{
          "& .MuiDataGrid-row:hover": {
            backgroundColor: "inherit",
            cursor: "pointer",
          },
          "& .MuiDataGrid-cell:focus": {
            outline: "none",
          },
          "& .MuiDataGrid-columnHeader:focus": {
            outline: "none",
          },
          "& .MuiDataGrid-cellEmpty": {
            display: "none",
          },
        }}
        disableColumnMenu
        disableColumnResize
        rowSelectionModel={{
          type: "include",
          ids: new Set(currentCoachRef.current ? [currentCoachRef.current] : []),
        }}
        onRowSelectionModelChange={(model) => {
          if (model.ids.size > 0) {
            setSelectedId(Array.from(model.ids)[0] as number)
            currentCoachRef.current = Array.from(model.ids)[0] as number
          }
        }}
        pagination
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        slots={{ pagination: Pagination }}
      />
    </div>
  )
}
